import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Grid, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Formik } from 'formik';
import classNames from 'classnames';
import { useHistory } from 'react-router';

import { PaddedPaper, ValidatedSwitch } from 'components';
import {
  AssignmentOperator,
  AssignmentTargetUser,
  AssignmentInput,
  AssignmentFragment,
  useUpdateAssignmentMutation,
} from 'api';
import { useNotify, useProtectedClient } from 'hooks';
import * as paths from 'paths';

import { AssignmentFormSchema } from 'features/admin/validations';
import AssignmentForm from 'features/admin/components/Assignments/Partials/Form';
import Actions from 'features/admin/components/Assignments/Partials/Actions';

interface Props {
  assignment: AssignmentFragment;
}

const useStyles = makeStyles(theme => ({
  divider: {
    margin: theme.spacing(2, -5),
  },
  switchLabel: {
    fontSize: theme.spacing(2),
    fontWeight: 'bold',
  },
}));

const AssignmentEdit: FC<Props> = ({ assignment }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const client = useProtectedClient();
  const notify = useNotify();
  const history = useHistory();

  const { mutate: updateAssignment, isLoading } = useUpdateAssignmentMutation({
    onError: notify.mutationError,
    onSuccess: data => {
      const { updateAssignment } = data;
      history.push(paths.adminSettingsAssignments);
      notify.info({
        message: t('admin:assignmentUpdateMessage', { assignment: updateAssignment.title }),
      });
    },
  });

  const initialValues: AssignmentInput = {
    client_id: client.id,
    title: '',
    active: true,
    users_required_ids: [],
    users_suggested_ids: [],
    groups_required_ids: [],
    groups_suggested_ids: [],
    target_user: AssignmentTargetUser.All,
    rule: {
      all_users: false,
      operator: AssignmentOperator.And,
      department_ids: [],
      location_ids: [],
    },
  };

  function getValues() {
    const assignmentInput = initialValues;

    assignmentInput.title = assignment?.title;
    assignmentInput.groups_required_ids = assignment?.groups_required_objects.map(
      group => group.id
    );
    assignmentInput.groups_suggested_ids = assignment?.groups_suggested_objects.map(
      group => group.id
    );
    assignmentInput.users_required_ids = assignment?.users_required_objects.map(user => user.id);
    assignmentInput.users_suggested_ids = assignment?.users_suggested_objects.map(user => user.id);

    assignmentInput.target_user = assignment?.target_user;
    assignmentInput.rule = assignment?.rule;
    assignmentInput.active = assignment?.active;

    return assignmentInput;
  }

  function onSubmit(values: AssignmentInput) {
    const data = values;

    delete data.client_id;

    updateAssignment({
      client_id: client.id,
      id: assignment.id,
      data,
    });
  }

  return (
    <Formik onSubmit={onSubmit} initialValues={getValues()} validationSchema={AssignmentFormSchema}>
      {({ values }) => (
        <Fragment>
          <PaddedPaper padding="16px 40px">
            <Grid container justify="space-between" alignItems="center">
              <Grid item>
                <Typography variant="h4">{t('admin:assignmentsFormEdit')}</Typography>
              </Grid>

              <Tooltip title={t('admin:assignmentSwitchDescription')} placement="bottom">
                <Grid item>
                  <ValidatedSwitch
                    color="primary"
                    name="active"
                    label={t('common:Enabled')}
                    labelPlacement="start"
                    labelClasses={{ label: classNames({ [classes.switchLabel]: values.active }) }}
                  />
                </Grid>
              </Tooltip>
            </Grid>

            <Divider className={classes.divider} />
            <AssignmentForm assignment={assignment} />
          </PaddedPaper>
          <Actions working={isLoading} id={assignment.id} />
        </Fragment>
      )}
    </Formik>
  );
};

export default AssignmentEdit;
